<script setup lang="ts">
/** TMP083 — Statement Containing Particulars of Shares Allotted Otherwise Than for Cash.
 *  실제 변수: company, total_new_shares, share_price, creditor_name, allotment_date,
 *  capitalized_amount, statement_date, allottee loop, Signers.
 *  토글: clause_2_applicable, clause_3_applicable, statement_resolution_basis(dividend|reserve). */
import { computed, inject } from 'vue';
import { OTC_DOC_CTX } from './otcContext';
import OtcDocVarField from './OtcDocVarField.vue';
import OtcAllotteeTable from './parts/OtcAllotteeTable.vue';
import OtcSigners from './parts/OtcSigners.vue';

const ctx = inject(OTC_DOC_CTX)!;
const clause2 = computed(() => ctx.flag('clause_2_applicable'));
const clause3 = computed(() => ctx.flag('clause_3_applicable'));
const basis = computed(() => ctx.choiceOf('statement_resolution_basis') || 'dividend');
function toggleBasis() {
  ctx.setChoice('statement_resolution_basis', basis.value === 'dividend' ? 'reserve' : 'dividend');
}
</script>

<template>
  <div class="allot-doc">
    <div class="doc-head">
      <div class="doc-meta">Company Registration No. <OtcDocVarField field-key="company_registration_no" /></div>
      <div class="doc-company" style="font-size:14px">STATEMENT CONTAINING PARTICULARS OF SHARES ALLOTTED<br />OTHERWISE THAN FOR CASH</div>
      <div class="doc-company" style="margin-top:6px"><OtcDocVarField field-key="company_name" /></div>
    </div>

    <p class="doc-p">
      1. &nbsp; The allotment of <OtcDocVarField field-key="total_new_shares" /> shares of
      RM<OtcDocVarField field-key="share_price" /> each to <OtcDocVarField field-key="creditor_name" />
      on the <OtcDocVarField field-key="allotment_date" /> was made pursuant to a contract not reduced to writing.
    </p>

    <p class="doc-p">
      <span class="doc-toggle" @click="ctx.toggleFlag('clause_2_applicable')">{{ clause2 ? '✓ clause 2 applicable' : '☐ clause 2' }}</span>
      <template v-if="clause2"> *2. &nbsp; Particulars sufficient to show the entitlement of the allottees to the allotment of shares where the allotment was made pursuant to a contract not reduced to writing are as follows : †</template>
    </p>

    <p class="doc-p">
      <span class="doc-toggle" @click="ctx.toggleFlag('clause_3_applicable')">{{ clause3 ? '✓ clause 3 applicable' : '☐ clause 3' }}</span>
      <template v-if="clause3"> *3. &nbsp; The particulars of the resolution or other authority by virtue of which the allotment of shares was made are as follows :-
        <span class="doc-toggle" @click="toggleBasis">{{ basis === 'dividend' ? 'an allotment was made in satisfaction of a dividend declared in favour of, but not payable in cash to, the shareholders.' : 'an account or reserve was applied directly in paying up shares' }}</span>
      </template>
    </p>

    <p class="doc-p">4. &nbsp; The particulars of the consideration in respect of which the allotment of shares was made are as follows:</p>

    <p class="doc-lbl-title">Non-Cash Allotment</p>
    <OtcAllotteeTable />

    <p class="doc-p" style="margin-top:12px">
      THAT the <OtcDocVarField field-key="total_new_shares" /> shares allotted to
      <OtcDocVarField field-key="creditor_name" /> is/are satisfied by the conversion of amount owing to
      <OtcDocVarField field-key="creditor_name" /> totalling RM<OtcDocVarField field-key="capitalized_amount" />.
    </p>

    <p class="doc-p" style="margin-top:10px"><b>Dated this</b> <OtcDocVarField field-key="statement_date" /></p>
    <OtcSigners var-key="statement" />

    <p class="doc-attn">
      <b>Attention:</b> It is an offence under section 591 of the Companies Act 2016 to make or authorize the making of a statement that a
      person knows is false or misleading and that person may be liable, upon conviction, to imprisonment for a term not exceeding ten years or
      to a fine not exceeding RM3million or to both.
    </p>
  </div>
</template>
